import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, FormControlLabel } from "@mui/material";
import { IFileWithMeta } from "react-dropzone-uploader";
import {
  UpfrontPaymentFinancialSummary,
  UpfrontPaymentFinancialSummaryRow,
  UpfrontPaymentFinancialSummaryRowHeader,
  UpfrontPaymentFinancialSummaryRowText,
  UpfrontPaymentFinancialSummaryTitle,
  UpfrontPaymentFinancialSummaryAttestation,
} from "../UpfrontPayment.styles";
import { GetTypographyContent } from "../../shared/Typography/Typography";

interface ConfirmationProps {
  medicaidInfo: {
    hasMedicaid?: "Yes" | "No";
    frontCardFileName?: string;
    backCardFileName?: string;
  };
  householdInfo?: {
    size?: number;
    income?: number;
    incomeDisplay?: string;
  };
  optionalQuestions?: {
    additionalCoverage?: boolean;
    locality?: string;
    extenuatingCircumstances?: string[];
  };
  rulesAccepted?: boolean;
  setRulesAccepted?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFAFile?: IFileWithMeta | null;
}

export const StepConfirmation: React.FC<ConfirmationProps> = ({
  medicaidInfo,
  householdInfo,
  optionalQuestions,
  rulesAccepted,
  setRulesAccepted,
  selectedFAFile,
}) => {
  const { t } = useTranslation();
  const yesNoValue = medicaidInfo.hasMedicaid
    ? t(
        `upfrontPayment.common.${medicaidInfo.hasMedicaid === "Yes" ? "yes" : "no"}`,
      )
    : t("upfrontPayment.financialAssistance.notProvided");

  return (
    <>
      <UpfrontPaymentFinancialSummary>
        <UpfrontPaymentFinancialSummaryTitle>
          {t("upfrontPayment.financialAssistance.informationProvided")}
        </UpfrontPaymentFinancialSummaryTitle>

        <UpfrontPaymentFinancialSummaryRow>
          <UpfrontPaymentFinancialSummaryRowHeader>
            {t("upfrontPayment.financialAssistance.haveMedicaid")}
          </UpfrontPaymentFinancialSummaryRowHeader>
          <UpfrontPaymentFinancialSummaryRowText>
            {yesNoValue}
          </UpfrontPaymentFinancialSummaryRowText>
        </UpfrontPaymentFinancialSummaryRow>

        {medicaidInfo.hasMedicaid === "Yes" ? (
          <>
            <UpfrontPaymentFinancialSummaryRow>
              <UpfrontPaymentFinancialSummaryRowHeader>
                {t("upfrontPayment.financialAssistance.frontMedicaidCard")}
              </UpfrontPaymentFinancialSummaryRowHeader>
              <UpfrontPaymentFinancialSummaryRowText>
                {medicaidInfo.frontCardFileName ||
                  t("upfrontPayment.financialAssistance.notUploaded")}
              </UpfrontPaymentFinancialSummaryRowText>
            </UpfrontPaymentFinancialSummaryRow>
            <UpfrontPaymentFinancialSummaryRow>
              <UpfrontPaymentFinancialSummaryRowHeader>
                {t("upfrontPayment.financialAssistance.backMedicaidCard")}
              </UpfrontPaymentFinancialSummaryRowHeader>
              <UpfrontPaymentFinancialSummaryRowText>
                {medicaidInfo.backCardFileName ||
                  t("upfrontPayment.financialAssistance.notUploaded")}
              </UpfrontPaymentFinancialSummaryRowText>
            </UpfrontPaymentFinancialSummaryRow>
          </>
        ) : (
          <>
            <UpfrontPaymentFinancialSummaryRow>
              <UpfrontPaymentFinancialSummaryRowHeader>
                {t("upfrontPayment.financialAssistance.householdSizeSummary")}
              </UpfrontPaymentFinancialSummaryRowHeader>
              <UpfrontPaymentFinancialSummaryRowText>
                {householdInfo?.size ||
                  t("upfrontPayment.financialAssistance.notProvided")}
              </UpfrontPaymentFinancialSummaryRowText>
            </UpfrontPaymentFinancialSummaryRow>
            <UpfrontPaymentFinancialSummaryRow>
              <UpfrontPaymentFinancialSummaryRowHeader>
                {t("upfrontPayment.financialAssistance.incomeSummary")}
              </UpfrontPaymentFinancialSummaryRowHeader>
              <UpfrontPaymentFinancialSummaryRowText>
                {householdInfo?.income
                  ? householdInfo.incomeDisplay
                  : t("upfrontPayment.financialAssistance.notProvided")}
              </UpfrontPaymentFinancialSummaryRowText>
            </UpfrontPaymentFinancialSummaryRow>
            {Boolean(optionalQuestions?.extenuatingCircumstances?.length) && (
              <UpfrontPaymentFinancialSummaryRow>
                <UpfrontPaymentFinancialSummaryRowHeader>
                  {t(
                    "upfrontPayment.financialAssistance.extenuatingCircumstancesLabel",
                  )}
                </UpfrontPaymentFinancialSummaryRowHeader>
                <UpfrontPaymentFinancialSummaryRowText
                  style={{ textWrap: "wrap" }}
                >
                  {optionalQuestions?.extenuatingCircumstances?.join(", ")}
                </UpfrontPaymentFinancialSummaryRowText>
              </UpfrontPaymentFinancialSummaryRow>
            )}
            {optionalQuestions?.locality && (
              <UpfrontPaymentFinancialSummaryRow>
                <UpfrontPaymentFinancialSummaryRowHeader>
                  {t("upfrontPayment.financialAssistance.locality")}
                </UpfrontPaymentFinancialSummaryRowHeader>
                <UpfrontPaymentFinancialSummaryRowText>
                  {optionalQuestions.locality}
                </UpfrontPaymentFinancialSummaryRowText>
              </UpfrontPaymentFinancialSummaryRow>
            )}
            {selectedFAFile && (
              <UpfrontPaymentFinancialSummaryRow>
                <UpfrontPaymentFinancialSummaryRowHeader>
                  {t("upfrontPayment.financialAssistance.document")}
                </UpfrontPaymentFinancialSummaryRowHeader>
                <UpfrontPaymentFinancialSummaryRowText>
                  {selectedFAFile.meta.name}
                </UpfrontPaymentFinancialSummaryRowText>
              </UpfrontPaymentFinancialSummaryRow>
            )}
          </>
        )}
      </UpfrontPaymentFinancialSummary>
      {medicaidInfo.hasMedicaid === "No" && setRulesAccepted && (
        <div>
          <UpfrontPaymentFinancialSummaryAttestation>
            I hereby certify that the information provided is true and accurate.
            I also certify that I do not carry any U.S. federal and state-funded
            health insurance (e.g., Medicare, Medicaid, Tricare,
            MedicareAdvantage). I understand and agree that BillionToOne
            reserves the right, at any time and without notice, to modify the
            application form, to modify or terminate this program, to audit my
            information or to request additional information.
          </UpfrontPaymentFinancialSummaryAttestation>
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => setRulesAccepted(e.target.checked)}
                checked={rulesAccepted}
                data-testid="terms-accepted-checkbox"
                sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
              />
            }
            label={GetTypographyContent({
              content: t("upfrontPayment.financialAssistance.iAccept"),
              size: "medium",
              textColor: "greyMedium",
            })}
          />
        </div>
      )}
    </>
  );
};
